import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function Register() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();

  // Password strength validation
  const checkPasswordStrength = (password) => {
    return {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[@$!%*?&#]/.test(password)
    };
  };

  const passwordStrength = checkPasswordStrength(formData.password);
  const isPasswordValid = Object.values(passwordStrength).every(v => v);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const response = await authAPI.register(formData);
      
      // Show success message about email verification
      if (response.data.user.role === 'user') {
        setSuccess('Registration successful! Please check your email to verify your account.');
        // Don't auto-login, let user verify email first
        setTimeout(() => {
          navigate('/login');
        }, 4000);
      } else {
        // Admin users can login directly (fetch user from server)
        await login(response.data.token);
        navigate('/admin');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card" style={{ maxWidth: '440px', margin: '40px auto' }}>
        <h2 style={{ marginBottom: '8px', textAlign: 'center', color: '#667eea', fontSize: '22px', fontWeight: '700' }}>
          Create Account
        </h2>
        <p style={{ textAlign: 'center', color: '#718096', fontSize: '13px', marginBottom: '24px' }}>
          Join us to start creating and taking quizzes
        </p>
        {error && <div className="error">{error}</div>}
        {success && (
          <div style={{
            padding: '12px 16px',
            borderRadius: '8px',
            marginBottom: '20px',
            background: '#f0fdf4',
            border: '1px solid #86efac',
            color: '#166534',
            fontSize: '14px'
          }}>
            {success}
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setPasswordFocused(true)}
                onBlur={() => setPasswordFocused(false)}
                style={{ 
                  paddingRight: '45px',
                  borderColor: formData.password && !isPasswordValid ? '#f56565' : undefined
                }}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '18px',
                  color: '#718096',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {showPassword ? '👁️' : '👁️‍🗨️'}
              </button>
            </div>
            
            {/* Password strength indicator */}
            {(passwordFocused || formData.password) && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f7fafc',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
                fontSize: '13px'
              }}>
                <div style={{ fontWeight: '600', marginBottom: '8px', color: '#2d3748' }}>
                  Password must contain:
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <PasswordRequirement 
                    met={passwordStrength.minLength} 
                    text="At least 8 characters"
                  />
                  <PasswordRequirement 
                    met={passwordStrength.hasUppercase} 
                    text="One uppercase letter (A-Z)"
                  />
                  <PasswordRequirement 
                    met={passwordStrength.hasLowercase} 
                    text="One lowercase letter (a-z)"
                  />
                  <PasswordRequirement 
                    met={passwordStrength.hasNumber} 
                    text="One number (0-9)"
                  />
                  <PasswordRequirement 
                    met={passwordStrength.hasSpecialChar} 
                    text="One special character (@$!%*?&#)"
                  />
                </div>
              </div>
            )}
          </div>
          <button 
            type="submit" 
            className="btn btn-primary" 
            style={{ width: '100%', marginTop: '8px', padding: '12px' }}
            disabled={loading || (formData.password && !isPasswordValid)}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
        </form>
        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#4a5568' }}>
          Already have an account? <Link to="/login" style={{ color: '#667eea', fontWeight: '600', textDecoration: 'none' }}>Sign In</Link>
        </p>
      </div>
    </div>
  );
}

// Password requirement indicator component
function PasswordRequirement({ met, text }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{
        fontSize: '16px',
        color: met ? '#48bb78' : '#cbd5e0',
        fontWeight: 'bold'
      }}>
        {met ? '✓' : '○'}
      </span>
      <span style={{ color: met ? '#2d3748' : '#718096' }}>
        {text}
      </span>
    </div>
  );
}

export default Register;
